import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { SEO } from '../components/SEO';
import { collectiveReadings } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CollectiveMay2026Page = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: email,
        source: 'collective-may-2026'
      });
      setSubscribed(true);
      setEmail('');
      toast.success('You\'re subscribed', {
        description: 'You\'ll receive each month\'s reflection in your inbox.'
      });
    } catch (error) {
      toast.error('Something went wrong', {
        description: 'Please try again.'
      });
    }
    setIsSubmitting(false);
  };

  const previousReadings = collectiveReadings.filter(r => r.slug !== 'may-2026');

  return (
    <div className="bg-[var(--color-charcoal)]">
      <SEO 
        title="May 2026 Collective Reflection | The Soul Script"
        description="May 2026 reflection exploring uncertainty, clarity, and grounded decision-making."
        path="/collective/may-2026"
      />

      {/* Full-Width Hero */}
      <section className="relative min-h-[70vh] flex items-end">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1761755227421-b5e07efec0be?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-charcoal)] via-[var(--color-charcoal)]/70 to-[var(--color-charcoal)]/20" />
        </div>
        
        <div className="relative w-full pb-16 pt-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
            >
              <Link 
                to="/collective" 
                className="inline-flex items-center gap-2 text-[var(--color-gold)] text-sm tracking-[0.2em] uppercase mb-8 hover:opacity-80 transition-opacity"
                data-testid="back-to-collective"
              >
                <ArrowLeft className="w-4 h-4" />
                The Collective
              </Link>
              
              <p className="text-[var(--color-gold)] text-lg tracking-[0.2em] uppercase mb-4">
                May 2026
              </p>
              
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl max-w-4xl leading-[1.05]" data-testid="may-2026-title">
                When Clarity Is Not Immediate
              </h1>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Before You Read */}
      <section className="py-16 bg-[var(--color-dark-purple)]/8 border-y border-[var(--color-gold)]/10">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <p className="text-[var(--color-cream)]/70 leading-[2] text-lg italic">
              Pause.<br /><br />
              Take one slow breath. Let your body settle.<br /><br />
              This reading is not a prediction. It is a mirror.<br /><br />
              Notice what resonates. Leave what does not.
            </p>
            <p className="text-[var(--color-gold)] text-lg mt-8 font-serif">
              Ask yourself quietly:<br />
              What feels unclear — and how am I responding to that uncertainty?
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <article className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          {/* The Energy of May */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-gold)] sticky top-32">
                The Energy of May
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p>May does not offer immediate clarity.</p>
              <p>It slows things down.</p>
              <p>Where March asked for action and April asked for truth, May asks you to sit with what is still unresolved.</p>
              <p>There may be moments where things feel unclear. Not because nothing is happening, but because you are being asked to look more closely.</p>
              <p className="text-[var(--color-cream)]">At the centre of this month is duality.</p>
              <p>What you see on the surface, and what you sense underneath it. What feels familiar, and what no longer quite fits.</p>
              <p>You may question decisions, relationships, or directions you were previously certain about.</p>
              <p className="text-[var(--color-cream)]">This is not regression.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">It is refinement.</p>
              <p>Not everything becomes clear at once. Some things need time, distance, and attention.</p>
              <p className="text-[var(--color-cream)]">Clarity is not absent. It is forming.</p>
            </div>
          </motion.section>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-24">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[var(--color-gold)]/20 to-transparent" />
          </div>

          {/* What Is Being Released */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-bottle-green-light)] sticky top-32">
                What Is Being Released
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p>This month draws attention to your foundations.</p>
              <p>The structures you rely on. The values you operate from. The assumptions you have carried forward without re-examining.</p>
              <p className="text-[var(--color-cream)]">You may begin to notice where you have been compromising your own truth.</p>
              <p>Where you have chosen comfort over alignment. Where you have stayed with something because it was known, not because it was right.</p>
              <p>There may also be a tendency to get stuck in your own perspective.</p>
              <p>To overanalyse without stepping back. To stay inside the loop of your own thinking.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">What is needed here is perspective.</p>
              <p>Distance that allows you to see more clearly.</p>
              <p>There is also a need for discernment.</p>
              <p>Not everything needs to be shared, explained, or resolved immediately.</p>
              <p className="text-[var(--color-cream)]">Some parts of your life are still forming. They require space, not exposure.</p>
              <p>Release what no longer holds.</p>
              <p className="text-[var(--color-cream)]">Be more selective about what you carry forward.</p>
            </div>
          </motion.section>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-24">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[var(--color-gold)]/20 to-transparent" />
          </div>

          {/* What Is Being Invited */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-gold)] sticky top-32">
                What Is Being Invited
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p>What replaces this is not dramatic change.</p>
              <p className="text-[var(--color-cream)]">It is stability.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">A return to yourself.</p>
              <p>A clearer relationship with your own space, your time, your environment.</p>
              <p>You may feel the need to simplify.</p>
              <p>To clear physical clutter. To step back from unnecessary commitments. To reduce noise.</p>
              <p className="text-[var(--color-cream)]">This is not avoidance. It is recalibration.</p>
              <p>Your external environment affects your internal clarity more than you realise.</p>
              <p>There is also a need to bring awareness into action.</p>
              <p>Reflection without action leads to stagnation. Action without reflection leads to repetition.</p>
              <p className="text-[var(--color-cream)]">This month asks for both.</p>
              <p>Not large changes.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">Small, deliberate adjustments that bring you back into alignment.</p>
            </div>
          </motion.section>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-24">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[var(--color-gold)]/20 to-transparent" />
          </div>

          {/* Guidance */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-cream)]/60 sticky top-32">
                Guidance
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p className="text-[var(--color-cream)]">The most useful thing you can do this month is create moments of stillness.</p>
              <p>Not passive stillness. Active awareness.</p>
              <p>Time where you step away from input and observe what is actually happening within you.</p>
              <p className="text-[var(--color-cream)]">This can be simple.</p>
              <p>Sitting without distraction.</p>
              <p>Walking without a phone.</p>
              <p>Paying attention to your breath.</p>
              <p>Or working with your hands.</p>
              <p>Writing. Drawing. Rearranging your space.</p>
              <p className="text-[var(--color-cream)]">Activities that require presence, not performance.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">These practices are not separate from clarity.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">They create it.</p>
              <p>There is also a shift in how you approach uncertainty.</p>
              <p>Instead of trying to resolve it immediately, stay with it long enough to understand it.</p>
              <p className="text-[var(--color-cream)]">Uncertainty is not the absence of direction.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">It is the stage before direction becomes clear.</p>
            </div>
          </motion.section>

        </div>
      </article>

      {/* Inquiry */}
      <section className="py-20 lg:py-28 bg-[var(--color-bottle-green)]/8 border-y border-[var(--color-gold)]/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-serif text-2xl text-[var(--color-gold)] mb-8">Inquiry</h2>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-[var(--color-cream)] leading-[1.4] mb-6">
              Where am I trying to force clarity instead of allowing it to develop?
            </p>
            <p className="text-[var(--color-cream)]/60 text-lg leading-relaxed max-w-2xl mx-auto">
              Let the answer come without pressure.<br />
              Then take one small step that brings you back into alignment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <h2 className="font-serif text-2xl text-[var(--color-gold)] mb-8">Closing</h2>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              May is not a month for rushing decisions.
            </p>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              It is a month for seeing more clearly before you move.
            </p>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              You do not need to have everything figured out.
            </p>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              But you do need to pay attention.
            </p>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              Stay present.<br />
              Simplify where needed.<br />
              Adjust where necessary.
            </p>
            <p className="font-serif text-2xl md:text-3xl text-[var(--color-cream)] mt-8">
              From there, the next step will make sense.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Continue the Work */}
      <section className="py-20 bg-[var(--color-charcoal-light)] border-t border-[var(--color-gold)]/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Tarot Session */}
              <div>
                <h3 className="font-serif text-2xl text-[var(--color-cream)] mb-4">Continue the Work</h3>
                <p className="text-[var(--color-cream)]/70 leading-[1.8] mb-6">
                  If this month's reflection stirred something you would like to explore more deeply, you are welcome to book a reflective tarot session.
                </p>
                <p className="text-[var(--color-cream)]/60 leading-[1.8] mb-6 text-sm">
                  Tarot here is not predictive. It is a grounded conversation that supports clarity, emotional resilience, and conscious choice.
                </p>
                <Link
                  to="/tarot"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-medium rounded-full hover:bg-[var(--color-gold-light)] transition-colors"
                  data-testid="may-book-tarot-btn"
                >
                  Book a Tarot Session
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Newsletter */}
              <div>
                <h3 className="font-serif text-2xl text-[var(--color-cream)] mb-4">Monthly Reflections</h3>
                <p className="text-[var(--color-cream)]/70 leading-[1.8] mb-6">
                  If you would like to receive each month's Collective Reflection directly in your inbox, you can leave your email below.
                </p>
                
                {subscribed ? (
                  <p className="text-[var(--color-gold)]" data-testid="may-subscribed-msg">Thank you. You'll receive the next reflection in your inbox.</p>
                ) : (
                  <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email address"
                      required
                      className="w-full px-5 py-3 bg-[var(--color-charcoal)] border border-[var(--color-gold)]/20 rounded-lg text-[var(--color-cream)] placeholder:text-[var(--color-cream)]/40 focus:outline-none focus:border-[var(--color-gold)]/50 transition-colors"
                      data-testid="collective-may-email"
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-6 py-3 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-medium rounded-lg hover:bg-[var(--color-gold-light)] transition-all duration-300 disabled:opacity-50"
                      data-testid="collective-may-submit"
                    >
                      {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                    </button>
                    <p className="text-xs text-[var(--color-cream)]/40 text-center">
                      One reflection per month. No noise.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Previous Reflections */}
      <section className="py-12 border-t border-[var(--color-gold)]/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <details className="group">
            <summary className="flex items-center justify-between cursor-pointer py-4 text-[var(--color-cream)]/60 hover:text-[var(--color-cream)] transition-colors" data-testid="may-previous-reflections-toggle">
              <span className="font-serif text-lg">Previous Collective Reflections</span>
              <ChevronDown className="w-5 h-5 group-open:rotate-180 transition-transform" />
            </summary>
            <div className="pt-4 pb-8 space-y-4">
              {previousReadings.length > 0 ? (
                previousReadings.map((reading) => (
                  <Link
                    key={reading.id}
                    to={`/collective/${reading.slug}`}
                    className="block p-4 bg-[var(--color-charcoal-light)] rounded-lg hover:bg-[var(--color-dark-purple)]/20 transition-colors"
                  >
                    <p className="text-[var(--color-gold)] text-sm mb-1">{reading.month}</p>
                    <p className="text-[var(--color-cream)]">{reading.title}</p>
                  </Link>
                ))
              ) : (
                <p className="text-[var(--color-cream)]/50 italic">No previous reflections yet.</p>
              )}
            </div>
          </details>
        </div>
      </section>
    </div>
  );
};
